import React, { useState } from 'react';
import { SeatAPIModel, SeatReservationStatus } from '@/apiModels/SeatReservation';
import Modal from 'react-modal';
import theme from '@/data/theme';
import {
  StyledInfoModalTableDetailLabel,
  StyledInfoModalTableDetailValue,
} from '@/components/ui/Modals/ReservationInfoModal/ReservationInfoModal.styled.ts';
import {
  BookingInforTableDetailValue,
  StyledBookingDetailsWrapper,
  StyledBookingInformation,
  StyledBookingInformationWrapper,
  StyledColumn,
  StyledInformationWrapper,
  StyledSeatStatusButton,
  StyledSubHeaderWrapper,
} from '@/components/ui/Modals/BookingStatusModal/BookingStatusModal.styled.ts';
import {
  StyledButtonWrapper,
  StyledEditModalButtonWrapper,
  StyledFilledBorderButton,
  StyledModalHeader,
  StyledModalTitleButtonWrapper,
  StyledModalTitleWrapper,
} from '@/components/ui/Modals/EditTableModal/EditTableModal.styled.ts';
import ModalCloseButton from '@/components/ui/ModalCloseButton';
import EditTableModal from '@/components/ui/Modals/EditTableModal';
import TableBookingModal from '@/components/ui/Modals/TableBookingModal';
import EditBookingModal from '@/components/ui/Modals/EditBookingModal';
import EditIcon from '@/components/icons/EditIcon';
import { StyledModalCloseButtonWrapper } from '@/components/ui/ModalCloseButton/ModalCloseButton.styled.ts';
import ReleaseSeatModal from '@/components/ui/Modals/ReleaseSeatModal';
import ToggleTableModal from '@/components/ui/Modals/TableActionModal';

interface IBookingStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  seatInformation: SeatAPIModel;
  eventId: string;
  seatId: string;
}

const SeatBookingStatusModal: React.FunctionComponent<IBookingStatusModalProps> = ({
  isOpen,
  onClose,
  seatInformation,
  eventId,
  seatId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isTableBookingModalOpen, setIsTableBookingModalOpen] = useState(false);
  const [isBookingEditModalOpen, setIsBookingEditModalOpen] = useState(false);
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isEnableModalOpen, setIsEnableModalOpen] = useState(false);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);

  const handleModalClose = () => {
    onClose();
    setIsEditing(false);
    setIsLoading(true);
  };

  const handleEditTableClick = () => {
    setIsEditing(true);
  };

  const handleEditTableBookingClick = () => {
    setIsBookingEditModalOpen(true);
  };

  const handleEnableTableClick = () => {
    setIsEnableModalOpen(true);
  };

  const handleDisableTableClick = () => {
    setIsDisableModalOpen(true);
  };
  const handleReleaseSeatClick = () => {
    setIsReleaseModalOpen(true);
  };

  const handleSeatAction = () => {
    if (seatInformation.status === SeatReservationStatus.AVAILABLE) {
      setIsTableBookingModalOpen(true);
    } else if (seatInformation.status === SeatReservationStatus.DISABLED) {
      handleEnableTableClick();
    } else if (seatInformation.status === SeatReservationStatus.BOOKED) {
      handleReleaseSeatClick();
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleModalClose}
        shouldCloseOnOverlayClick={false}
        style={{
          content: {
            maxWidth: '320px',
            minWidth: '296px',
            height: 'fit-content',
            borderRadius: '10px',
            padding: '25px 10px',
            inset: '0',
            margin: 'auto',
            boxShadow: '0px 2px 6px 0px rgba(134, 92, 208, 0.50)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            fontFamily: 'Poppins',
          },
          overlay: {
            background: theme.colors.modalBackground,
          },
        }}
      >
        <StyledModalTitleWrapper>
          <StyledModalHeader>Table:&nbsp;{seatInformation.serial_no}</StyledModalHeader>
          <StyledModalTitleButtonWrapper>
            {seatInformation.status === SeatReservationStatus.AVAILABLE && (
              <StyledModalCloseButtonWrapper onClick={handleEditTableClick}>
                <EditIcon />
              </StyledModalCloseButtonWrapper>
            )}
            {seatInformation.status === SeatReservationStatus.DISABLED && (
              <StyledModalCloseButtonWrapper onClick={handleEditTableClick}>
                <EditIcon />
              </StyledModalCloseButtonWrapper>
            )}

            {seatInformation.status === SeatReservationStatus.BOOKED &&
              seatInformation?.reservation_details?.booked_by?.toLowerCase() == 'organizer' && (
                <StyledModalCloseButtonWrapper onClick={handleEditTableBookingClick}>
                  <EditIcon />
                </StyledModalCloseButtonWrapper>
              )}

            <ModalCloseButton onClose={onClose} />
          </StyledModalTitleButtonWrapper>
        </StyledModalTitleWrapper>
        <StyledSubHeaderWrapper>
          <div>
            <StyledInfoModalTableDetailLabel>Total Amount:&nbsp;</StyledInfoModalTableDetailLabel>
            <StyledInfoModalTableDetailValue>
              <StyledInfoModalTableDetailValue>
                Rs. {Number(seatInformation.price).toLocaleString('en-IN')}
              </StyledInfoModalTableDetailValue>
            </StyledInfoModalTableDetailValue>
          </div>
          <div>
            <StyledInfoModalTableDetailLabel>Pax:&nbsp;</StyledInfoModalTableDetailLabel>
            <StyledInfoModalTableDetailValue>{seatInformation.pax}</StyledInfoModalTableDetailValue>
          </div>
        </StyledSubHeaderWrapper>
        <StyledBookingInformation
          dangerouslySetInnerHTML={{
            __html: seatInformation.features || '',
          }}
        />
        {seatInformation.status === SeatReservationStatus.BOOKED &&
          seatInformation?.reservation_details?.booked_by?.toLowerCase() !== 'organizer' && (
            <StyledBookingInformationWrapper style={{ display: 'flex', gap: '5px' }}>
              <StyledModalHeader> Booking Information</StyledModalHeader>
              <div>
                <StyledInfoModalTableDetailLabel>Booked by:&nbsp;</StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details.reserved_by || '-'}&nbsp; (
                  {seatInformation.reservation_details?.booked_by || '-'})
                </StyledInfoModalTableDetailValue>
              </div>
              <div>
                <StyledInfoModalTableDetailLabel>Booked For:&nbsp;</StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details?.booked_for || '-'}
                </StyledInfoModalTableDetailValue>
              </div>
              <div>
                <StyledInfoModalTableDetailLabel>Phone:&nbsp;</StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details?.phone_number || '-'}
                </StyledInfoModalTableDetailValue>
              </div>
              <div>
                <StyledInfoModalTableDetailLabel>
                  Booked Code:&nbsp;
                </StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details?.reservation_code || '-'}
                </StyledInfoModalTableDetailValue>
              </div>
            </StyledBookingInformationWrapper>
          )}
        {seatInformation?.reservation_details?.booked_by?.toLowerCase() === 'organizer' && (
          <StyledInformationWrapper>
            <StyledModalHeader> Booking Information</StyledModalHeader>
            <StyledBookingDetailsWrapper>
              <div>
                <StyledInfoModalTableDetailLabel>Booked by:&nbsp;</StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details.reserved_by || '-'}&nbsp; (
                  {seatInformation.reservation_details?.booked_by || '-'})
                </StyledInfoModalTableDetailValue>
              </div>
              <div>
                <StyledInfoModalTableDetailLabel>Booked for:&nbsp;</StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details?.booked_for || '-'}
                </StyledInfoModalTableDetailValue>
              </div>
              <div>
                <StyledInfoModalTableDetailLabel>
                  Phone Number:&nbsp;
                </StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details?.phone_number || '-'}
                </StyledInfoModalTableDetailValue>
              </div>
              <div>
                <StyledInfoModalTableDetailLabel>Email:&nbsp;</StyledInfoModalTableDetailLabel>
                <StyledInfoModalTableDetailValue>
                  {seatInformation.reservation_details?.email || '-'}
                </StyledInfoModalTableDetailValue>
              </div>
              <StyledColumn>
                <StyledInfoModalTableDetailLabel>Remarks:&nbsp;</StyledInfoModalTableDetailLabel>
                <BookingInforTableDetailValue>
                  {seatInformation.reservation_details?.remarks || '-'}
                </BookingInforTableDetailValue>
              </StyledColumn>
            </StyledBookingDetailsWrapper>
          </StyledInformationWrapper>
        )}
        <StyledButtonWrapper>
          {seatInformation.status === SeatReservationStatus.AVAILABLE && (
            <StyledEditModalButtonWrapper>
              <StyledFilledBorderButton onClick={handleDisableTableClick}>
                Disable
              </StyledFilledBorderButton>
            </StyledEditModalButtonWrapper>
          )}

          <StyledSeatStatusButton
            status={seatInformation.status}
            onClick={handleSeatAction}
            bookedBy={seatInformation?.reservation_details?.booked_by}
            disabled={
              isLoading ||
              (seatInformation.status !== SeatReservationStatus.AVAILABLE &&
                seatInformation.status !== SeatReservationStatus.BOOKED &&
                seatInformation.status !== SeatReservationStatus.DISABLED) ||
              (seatInformation.status === SeatReservationStatus.BOOKED &&
                (seatInformation?.reservation_details?.booked_by ?? '')
                  .toString()
                  .trim()
                  .toLowerCase() === 'revel')
            }
          >
            {isLoading
              ? 'Loading...'
              : seatInformation.status === SeatReservationStatus.BOOKED &&
                  (seatInformation?.reservation_details?.booked_by ?? '')
                    .toString()
                    .trim()
                    .toLowerCase() === 'organizer'
                ? 'Release Seat'
                : seatInformation.status === SeatReservationStatus.BOOKED &&
                    (seatInformation?.reservation_details?.booked_by ?? '')
                      .toString()
                      .trim()
                      .toLowerCase() === 'revel'
                  ? 'Booked'
                  : seatInformation.status === SeatReservationStatus.COMPLETED
                    ? 'Completed'
                    : seatInformation.status === SeatReservationStatus.DISABLED
                      ? 'Enable Seat'
                      : seatInformation.status === SeatReservationStatus.HOLD
                        ? 'Hold'
                        : seatInformation.status === SeatReservationStatus.AVAILABLE
                          ? 'Book'
                          : 'Select Table'}
          </StyledSeatStatusButton>
        </StyledButtonWrapper>
        <TableBookingModal
          isOpen={isTableBookingModalOpen}
          onClose={() => {
            setIsTableBookingModalOpen(false);
            onClose();
          }}
          seatId={seatId}
          eventId={eventId}
          seatInformation={seatInformation}
          onSuccess={() => {
            setIsTableBookingModalOpen(false);
            onClose();
          }}
          isLoading={isLoading}
        />
        <EditTableModal
          seatId={seatId}
          onClose={() => {
            setIsEditing(false);
            onClose();
          }}
          isLoading={isLoading}
          eventId={eventId}
          isOpen={isEditing}
          seatInformation={seatInformation}
        />
        <ToggleTableModal
          action="disable"
          isOpen={isDisableModalOpen}
          onClose={() => {
            setIsDisableModalOpen(false);
            onClose();
          }}
          seatInformation={seatInformation}
          eventId={eventId}
        />
        <ToggleTableModal
          action="enable"
          isOpen={isEnableModalOpen}
          onClose={() => {
            setIsEnableModalOpen(false);
            onClose();
          }}
          seatInformation={seatInformation}
          eventId={eventId}
        />
        <EditBookingModal
          seatId={seatId}
          onClose={() => {
            setIsBookingEditModalOpen(false);
            onClose();
          }}
          eventId={eventId}
          isOpen={isBookingEditModalOpen}
          seatInformation={seatInformation}
          onSuccess={onClose}
          isLoading={isLoading}
        />
        <ReleaseSeatModal
          seatInformation={seatInformation}
          onClose={() => {
            setIsReleaseModalOpen(false);
            onClose();
          }}
          eventId={eventId}
          isOpen={isReleaseModalOpen}
        />
      </Modal>
    </>
  );
};

export default SeatBookingStatusModal;
